import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ErrorCode, type GitProgressTrigger } from '@apm/contracts';
import { errorMessage } from '../../lib/error-message';
import { ApiRequestError } from '../../lib/http';
import {
  type ProgressCommitChoice,
  ProgressCommitDialog,
  commitKey,
} from './components/progress-commit-dialog';
import { progressCommitsApi, progressCommitsKeys } from './progress-commits-api';

/** Một thao tác tiến độ cần hỏi commit. */
export interface ProgressCommitAsk {
  readonly projectId: string;
  readonly taskId: string;
  readonly trigger: GitProgressTrigger;
  /** Bắt buộc khi `trigger = 'checklist_item'`. */
  readonly checklistItemId?: string | undefined;
  /** "Tick: ‹mục›" / "Hoàn thành: ‹việc›" — dòng thứ hai của hộp. */
  readonly heading: string;
}

interface Pending {
  readonly ask: ProgressCommitAsk;
  readonly linked: ReadonlySet<string>;
  readonly run: () => unknown;
  readonly onCancel?: (() => void) | undefined;
}

/** Lần thử gửi bản báo khi thao tác chưa kịp ghi (thao tác `mutate` không trả Promise). */
const REPORT_ATTEMPTS = 5;
const REPORT_RETRY_MS = 400;

/**
 * Gắn cập nhật tiến độ với commit — APM-PCOM-c, docs/plan-progress-commits.md §4.3.
 *
 * `ask(params, run, onCancel)`: dự án chưa bật (hoặc người xem không có `git:read`)
 * ⇒ chạy `run` ngay. Bật ⇒ mở `ProgressCommitDialog`; người dùng chọn commit hoặc
 * "Không có commit" ⇒ **chạy thao tác trước**, xong mới gửi bản báo (§0: thao tác
 * hỏng thì không có bản báo mồ côi). ✕ ⇒ `onCancel`, thao tác không chạy.
 *
 * `run` trả Promise (`mutateAsync`) thì chờ nó; trả `void` (`mutate`) thì bản báo gửi
 * ngay và thử lại vài lần khi server nói "chưa tick / chưa xong" — thao tác đang bay.
 */
export function useProgressCommitGuard(): {
  readonly ask: (params: ProgressCommitAsk, run: () => unknown, onCancel?: () => void) => void;
  readonly dialog: ReactNode;
} {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);

  const ask = useCallback(
    (params: ProgressCommitAsk, run: () => unknown, onCancel?: () => void): void => {
      void (async () => {
        const status = await client
          .fetchQuery({
            queryKey: progressCommitsKeys.status(params.projectId),
            queryFn: () => progressCommitsApi.status(params.projectId),
            staleTime: 60_000,
          })
          .catch(() => null);
        if (!status?.effective) {
          run();
          return;
        }
        // Commit đã báo cho đúng thao tác này — hộp đánh dấu "đã gắn".
        const reports = await client
          .fetchQuery({
            queryKey: progressCommitsKeys.reports(params.projectId, params.taskId),
            queryFn: () => progressCommitsApi.reports(params.projectId, params.taskId),
            staleTime: 30_000,
          })
          .catch(() => null);
        const linked = new Set(
          (reports?.reports ?? [])
            .filter(
              (report) =>
                report.trigger === params.trigger &&
                (params.trigger !== 'checklist_item' ||
                  report.checklistItemId === params.checklistItemId),
            )
            .flatMap((report) =>
              report.commits.map((commit) => commitKey(commit.repositoryId, commit.sha)),
            ),
        );
        setPending({ ask: params, linked, run, onCancel });
      })();
    },
    [client],
  );

  const submit = (current: Pending, choice: ProgressCommitChoice): void => {
    setPending(null);
    const { ask: params } = current;
    const body = {
      trigger: params.trigger,
      checklistItemId:
        params.trigger === 'checklist_item' ? (params.checklistItemId ?? null) : null,
      skipped: choice.skipped,
      commits: choice.skipped ? [] : [...choice.commits],
    };
    const send = async (attempt: number): Promise<void> => {
      try {
        const saved = await progressCommitsApi.report(params.projectId, params.taskId, body);
        client.setQueryData(progressCommitsKeys.reports(params.projectId, params.taskId), saved);
        // Lượt AI rà ngầm (APM-PCRV) vừa vào hàng đợi — chữ (i) hỏi lại ngay.
        void client.invalidateQueries({ queryKey: progressCommitsKeys.pendingReviews() });
        if (!choice.skipped) {
          toast.success(t('progressCommits.reported', { count: choice.commits.length }));
        }
      } catch (error) {
        const notYet =
          error instanceof ApiRequestError && error.code === ErrorCode.VALIDATION_FAILED;
        if (notYet && attempt < REPORT_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, REPORT_RETRY_MS * attempt));
          return send(attempt + 1);
        }
        // Thao tác không thành (đã báo lỗi ở nơi gọi) thì không có gì để báo commit.
        if (notYet) return;
        toast.error(t('progressCommits.reportFailed', { reason: errorMessage(error, t) }));
      }
    };
    void Promise.resolve()
      .then(() => current.run())
      .then(
        () => send(1),
        // Thao tác hỏng ⇒ nơi gọi đã báo lỗi; không gửi bản báo.
        () => undefined,
      );
  };

  const dialog = pending ? (
    <ProgressCommitDialog
      projectId={pending.ask.projectId}
      taskId={pending.ask.taskId}
      heading={pending.ask.heading}
      linked={pending.linked}
      onSubmit={(choice) => submit(pending, choice)}
      onCancel={() => {
        setPending(null);
        pending.onCancel?.();
      }}
    />
  ) : null;

  return { ask, dialog };
}
